import { mergeSxProps } from "@clipboard-health/ui-components";
import { getSafeAreaPaddings } from "@clipboard-health/ui-theme";
import { Box, type BoxProps, Fade } from "@mui/material";

import { getInsightBackground } from "./Insight/getInsightBackground";
import { type InsightWrapperVariant } from "./Insight/types";

export interface AnimatedInsightWrapperProps extends Omit<BoxProps, "component" | "onClick"> {
  insightType: InsightWrapperVariant;
  visible: boolean;
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
  /**
   * When true, keeps the component mounted even when not visible.
   * Useful for screenshot capture where we need DOM elements available.
   */
  keepMounted?: boolean;
}

export function AnimatedInsightWrapper(props: AnimatedInsightWrapperProps) {
  const { insightType, visible, sx, children, onClick, id, keepMounted = false, ...rest } = props;

  return (
    <Fade
      mountOnEnter={!keepMounted}
      unmountOnExit={!keepMounted}
      in={visible}
      appear={false}
      timeout={{ appear: 1000, enter: 1000, exit: 200 }}
    >
      <Box
        id={id ?? insightType}
        sx={mergeSxProps(
          (theme) => ({
            background: getInsightBackground(insightType),
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "stretch",
            position: "absolute",
            inset: 0,
            ...getSafeAreaPaddings({
              top: true,
              fallbackTop: theme.spacing(3),
            }),
          }),
          sx
        )}
        onClick={onClick}
        {...rest}
      >
        {children}
      </Box>
    </Fade>
  );
}
